import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-y-2 w-full" }
const _hoisted_2 = {
  key: 0,
  class: "flex w-full p-4 bg-yellow-50 rounded-md"
}
const _hoisted_3 = { class: "flex flex-row-reverse items-center justify-between w-full" }
const _hoisted_4 = { class: "flex items-center gap-x-4" }
const _hoisted_5 = {
  key: 1,
  class: "flex items-center gap-x-4"
}
const _hoisted_6 = { class: "flex" }
const _hoisted_7 = { class: "flex justify-start items-center" }
const _hoisted_8 = { class: "flex items-center justify-center mr-4 h-12 w-12 rounded-full bg-primary-color-100" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "flex items-center justify-center" }
const _hoisted_11 = { class: "text-base font-medium text-primary-color-600" }
const _hoisted_12 = { class: "text-sm text-gray-500" }
const _hoisted_13 = {
  key: 0,
  class: "flex gap-x-4 mt-2"
}
const _hoisted_14 = { class: "flex justify-end" }
const _hoisted_15 = { class: "flex flex-col justify-center items-center" }
const _hoisted_16 = { class: "flex items-center justify-center mr-4 h-12 w-12 rounded-full bg-yellow-100 mb-5" }

import { computed, onMounted, ref, provide, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { CalendarIcon, DocumentTextIcon, ClockIcon, ExclamationCircleIcon } from "@heroicons/vue/outline"
import { ExclamationIcon } from "@heroicons/vue/solid"
import GgmsTextarea from "@/components/GgmsTextarea.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsSeparator from "@/components/GgmsSeparator.vue"
import GgmsEmojiPicker from "@/components/GgmsEmojiPicker.vue"
import MessageTemplatesModal from "./message/MessageTemplatesModal.vue"
import GgmsBasicEmailParts from "@/components/GgmsBasicEmailParts.vue"
import GgmsWarningBanner from "@/components/GgmsWarningBanner.vue"
import DNDWarningComponent from "@/components/profile-details/do-not-disturb/DNDWarningComponent.vue"
import MarketingPreferencesWarningComponent from "@/components/profile-details/marketing-preferences/MarketingPreferencesWarningComponent.vue"
import GgmsCalendar from "@/components/GgmsCalendar.vue"
import { useAgencyStore } from "@/stores/agency"
import { useMessageStore } from "@/stores/message"
import { useConfigStore } from "@/stores/config"
import { useContactsStore } from "@/stores/contacts"
import { ToastService } from "@/shared/services/toast"
import { Message } from "@/shared/models/message"
import { Error } from "@/shared/models/error"
import { IntegrationName } from "@/shared/models/integration"
import { Phone } from "@/shared/models/phone"
import { Sender } from "@/shared/models/sender"
import { InboxContact } from "@/shared/models/inbox"
import { Contact } from "@/shared/models/contact"
import { getUser, getDisplayName } from "@/shared/utils/helpers"
import { formatters, getters, setters, predicates } from "@/shared/utils/dateUtils"
import { useWorkflowStore } from "@/stores/workflow"
import { CountryCode } from "libphonenumber-js"

interface Props {
    isWorkflow?: boolean
    hasEmails?: boolean
    hasTwilio?: boolean
    textMessage?: Message
    hasContact?: boolean
    hasSelectedContacts?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreatingMessage',
  props: {
    isWorkflow: { type: Boolean, default: false },
    hasEmails: { type: Boolean, default: false },
    hasTwilio: { type: Boolean, default: false },
    textMessage: {},
    hasContact: { type: Boolean, default: false },
    hasSelectedContacts: { type: Boolean, default: false }
  },
  emits: ["sendFromInbox", "saveTextMessage", "messageSent"],
  setup(__props: any, { emit: __emit }) {

const router = useRouter()

const emit = __emit

const props = __props

const agencyStore = useAgencyStore()
const contactStore = useContactsStore()
const messageStore = useMessageStore()
const toastService = new ToastService()
const configStore = useConfigStore()
const workflowStore = useWorkflowStore()
const route = useRoute()

provide("contactOverlayTarget-to", "creatingMessage")
provide("contactOverlayTarget-cc", "creatingMsgCcField")
provide("contactOverlayTarget-bcc", "creatingMsgCcField")

const messageTextArea = ref()
const message = ref("")
const recipients = ref<InboxContact[]>([])

const agency = computed(() => agencyStore.agency)
const agencyPhoneNumber = computed(
    () => (getUser().outboundPhoneNumber || agency.value?.agencyOutboundPhoneNumber) as Phone
)
const isTemplatesModalOpen = ref(false)
const isOpenScheduleMessageModal = ref(false)
const isOpenCustomSchedule = ref(false)
const smsCount = ref(0)

const customScheduleDate = ref("")
const customScheduleTime = ref("")
const isScheduleButtonDisabled = computed(() => !customScheduleDate.value || !customScheduleTime.value)

const scheduleDate = ref<Date>()
const scheduleOptions = ref([
    {
        text: "Tomorrow morning",
        date: setters.setTomoroowMorning(),
    },
])

const selectedSender = ref({
    name: agency.value.agencyName || "",
    phone: agencyPhoneNumber.value,
    type: "agency",
} as Sender)

const validationErrorMessage = computed(
    () => messageStore.validationError.fullMessage || messageStore.validationError.message
)
const twilioIntegration = computed(() =>
    configStore.integrations.find((i) => i.integrationName === IntegrationName.twilio)
)
const remainingDailySentMessagesLimit = computed(() => twilioIntegration.value?.remainingDailySentMessagesLimit || 500)
const remainingDailySentMessagesThreshold = computed(
    () => twilioIntegration.value?.remainingDailySentMessagesThreshold || 250
)
const isMessageLimitModalOpen = ref(false)
const emojis = ref("")
const isCreatingMessage = ref(false)

const disabledClass = computed(() =>
    isCreatingMessage.value ? "opacity-50 pointer-events-none cursor-not-allowed" : ""
)

const isButtonDisabled = computed(
    () =>
        (contactStore.haveAllDND ? !contactStore.sendAnywayDND : false) ||
        (contactStore.haveAllMP ? !contactStore.sendAnywayMP : false) ||
        agencyStore.agency?.isTestMode
)
const isSelectedDateToday = ref(true)
const minHour = computed(() => (isSelectedDateToday.value ? "now" : "today"))

function getStyleType() {
    if (props.hasEmails && props.hasTwilio) return "emailAndPhone"
    if (props.hasEmails) return "onlyEmail"
    if (props.hasTwilio) return "onlyPhone"
    return "primary"
}

async function createMessage() {
    try {
        isCreatingMessage.value = true
        if (props.isWorkflow) {
            emit("saveTextMessage")
        }
        const to = recipients.value
            .filter((contact) => contact.phone && contact.phone.number)
            .map((contact) => {
                const fullNameParts = contact.fullName?.split(" ")
                const reversedParts = fullNameParts?.reverse() || []
                const lastName = reversedParts.shift() || ""
                const firstName = reversedParts.reverse().join(" ") || ""

                return {
                    firstName,
                    lastName,
                    phone: {
                        number: contact.phone.number,
                        country: contact.phone.country || "RO",
                    },
                    _id: contact._id,
                }
            })
        const messagePayload: Message = {
            body: message.value,
            plain: message.value,
            from: {
                name: selectedSender.value?.name || "",
                number: selectedSender.value.phone?.number || "",
                country: selectedSender.value.phone?.country || "",
            },
            fromPhoneNumber: {
                number: selectedSender.value.phone?.number || "",
                country: selectedSender.value.phone?.country || "",
            },
            to,
            toCollections: recipients.value
                .filter((recipient) => recipient.type !== "contact")
                .map((collection) => collection._id),
            smsCount: smsCount.value * (to.length > 0 ? to.length : 1),
            sendAnywayDND: contactStore.sendAnywayDND || false,
            sendAnywayMP: contactStore.sendAnywayMP || false,
        }
        if (!messagePayload?.to?.length && !messagePayload?.toCollections?.length && !props.isWorkflow) {
            toastService.addToast({
                message: "Unable to send message. There are no contacts to send the message to.",
                type: "error",
            })
            return
        }

        emit("saveTextMessage")
        if (twilioIntegration.value) {
            messagePayload.integration = {
                _id: twilioIntegration.value._id,
                integrationName: twilioIntegration.value.integrationName,
                integrationType: twilioIntegration.value.integrationType,
            }
        }

        if (scheduleDate.value) {
            messagePayload.scheduledAt = scheduleDate.value
        }
        if (!scheduleDate.value && remainingDailySentMessagesLimit.value <= 0) {
            isMessageLimitModalOpen.value = true
            return
        }

        const contactId = contactStore.contact._id
        if (contactId) {
            if (props.isWorkflow) {
                workflowStore.messagePayload = messagePayload
            } else {
                const res = await messageStore.createMessage(messagePayload, contactStore.contact._id)
                emit("sendFromInbox")
                if (res) {
                    emit("messageSent")
                }
            }
        } else {
            if (props.isWorkflow) {
                workflowStore.messagePayload = messagePayload
            } else {
                const res = await messageStore.createMessage(messagePayload)
                emit("sendFromInbox")
                if (res) {
                    emit("messageSent")
                }
            }
        }

        message.value = ""
        scheduleDate.value = undefined
        if (contactStore.contact?.phones) {
            addRecipient(contactStore.contact)
            return
        }
        recipients.value = []
    } finally {
        await agencyStore.loadAgency()
        isCreatingMessage.value = false
        emit("saveTextMessage")
    }
}

function openScheduleMessageModal() {
    isOpenScheduleMessageModal.value = true
    const afternoonSchedule = setters.setThisAfternoon(scheduleOptions.value)
    if (afternoonSchedule) {
        scheduleOptions.value.push(afternoonSchedule)
    }
    setDateAndTime(new Date())
}

function closeScheduleMessageModal() {
    isOpenScheduleMessageModal.value = false
    isOpenCustomSchedule.value = false
    customScheduleDate.value = ""
    customScheduleTime.value = ""
}

function toggleCustomSchedule() {
    isOpenCustomSchedule.value = !isOpenCustomSchedule.value
}

function getCustomScheduledTime() {
    const date = new Date(customScheduleDate.value)
    const [hours, minutes] = getters.getHoursAndMinutes(customScheduleTime.value)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)
}

function saveScheduleMessage(date?: Date) {
    scheduleDate.value = date || getCustomScheduledTime()

    createMessage()
    closeScheduleMessageModal()
}

function disableValidationError() {
    messageStore.validationError = {} as Error
}

function closeMessageLimitModal() {
    isMessageLimitModalOpen.value = false
}

function setEmojiInTextarea(emoji: string) {
    emojis.value = emoji
}

function updateTemplate(template: { subject: string; body: string }) {
    message.value = template.body
}

function addRecipient(contact: Contact, index?: number) {
    const recipient = {
        _id: contact._id,
        fullName: getDisplayName(contact),
        doNotDisturb: contact?.doNotDisturb,
        type: "contact",
        marketingPreferences: contact?.marketingPreferences,
        phone: contact.phones?.[0],
    } as InboxContact

    if (!recipients.value.map((recipient) => recipient._id).includes(recipient._id) && recipient?.phone?.number) {
        index ? recipients.value.splice(index, 0, recipient) : recipients.value.push(recipient)
    }
}

function setSmsCount(count: number) {
    // eslint-disable-next-line no-console
    smsCount.value = count
}

function setDateAndTime(date: Date) {
    const isoDateInAgencyTimezone = formatters.formatDateToIso(date)
    const [datePart, timePart] = isoDateInAgencyTimezone.split("T")
    customScheduleDate.value = datePart
    customScheduleTime.value = timePart.slice(0, 5)
}

//--------------------------------------------COST VALIDATION--------------------------------------------//

const costExceeded = ref(false)

function setCostExceeded(isExceeded: boolean) {
    costExceeded.value = isExceeded
}

//--------------------------------------------COST VALIDATION END----------------------------------------//

onMounted(() => {
    messageStore.timelineMessageTextAreaRef = messageTextArea?.value?.textarea
    if ((contactStore.contact.phones?.length && !props.isWorkflow && route.name !== "inbox") || props.hasContact) {
        addRecipient(contactStore.contact)
    }
    if ((!props.isWorkflow && route.name !== "inbox") || props.hasSelectedContacts) {
        if (contactStore.tableState.selectedData && contactStore.tableState.selectedData.length > 0) {
            contactStore.tableState.selectedData.forEach((contact) => {
                addRecipient(contact)
            })
        }
    }
    if (props.isWorkflow && props.textMessage) {
        message.value = props.textMessage.body
        if (props.textMessage?.from) {
            selectedSender.value = {
                name: props.textMessage?.from?.name || "",
                phone: {
                    number: props.textMessage?.from?.number,
                    country: props.textMessage?.from?.country as CountryCode,
                },
            }
        }
    }
    setDateAndTime(new Date())
})

watch(
    () => contactStore.contact.phones,
    () => {
        const contactRecipientIndex = recipients.value.findIndex(
            (recipient) => recipient._id === contactStore.contact._id
        )
        if (contactRecipientIndex < 0) {
            return
        }
        addRecipient(contactStore.contact, contactRecipientIndex)
    },
    { deep: true }
)

watch(
    () => customScheduleDate.value,
    () => {
        isSelectedDateToday.value = predicates.isToday(new Date(customScheduleDate.value))
        if (
            customScheduleDate.value &&
            customScheduleTime.value &&
            predicates.isEarlierToday(customScheduleDate.value, customScheduleTime.value)
        ) {
            const newDate = new Date()
            customScheduleTime.value = `${newDate.getHours()}:${
                newDate.getMinutes() < 10 ? "0" : ""
            }${newDate.getMinutes()}`
        }
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(['flex flex-col justify-center items-center px-4 py-6 gap-y-3 relative', disabledClass.value])
    }, [
      _createVNode(GgmsBasicEmailParts, {
        recipients: recipients.value,
        "onUpdate:recipients": _cache[0] || (_cache[0] = ($event: any) => ((recipients).value = $event)),
        selectedSender: selectedSender.value,
        "onUpdate:selectedSender": _cache[1] || (_cache[1] = ($event: any) => ((selectedSender).value = $event)),
        isWorkflow: _ctx.isWorkflow,
        type: "sms"
      }, null, 8, ["recipients", "selectedSender", "isWorkflow"]),
      _createVNode(GgmsTextarea, {
        ref_key: "messageTextArea",
        ref: messageTextArea,
        modelValue: message.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((message).value = $event)),
        "template-string": emojis.value,
        placeholder: "Write your text",
        styleType: getStyleType(),
        rows: 11,
        "error-message": validationErrorMessage.value,
        invalid: !!validationErrorMessage.value,
        "is-count-available": true,
        "number-of-recipients": recipients.value.length,
        isForSMS: true,
        class: "w-full pb-0.5",
        onDisableValidationError: disableValidationError,
        onSmsCountChanged: setSmsCount,
        onCostExceeded: setCostExceeded
      }, null, 8, ["modelValue", "template-string", "styleType", "error-message", "invalid", "number-of-recipients"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(DNDWarningComponent, {
          recipients: recipients.value,
          showContactName: true,
          showCheckbox: true
        }, null, 8, ["recipients"]),
        _createVNode(MarketingPreferencesWarningComponent, {
          recipients: recipients.value,
          showContactName: true,
          showCheckbox: true,
          type: "sms"
        }, null, 8, ["recipients"])
      ]),
      (remainingDailySentMessagesThreshold.value <= 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(ExclamationIcon), { class: "h-6 w-6 text-yellow-400 mr-3" }),
            _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm font-medium text-yellow-800" }, [
              _createTextVNode(" You are about to exceed your Agency daily sending limit of 100. "),
              _createElementVNode("a", {
                href: "#",
                class: "underline underline-offset-[3px]"
              }, "Set up a Business Profile to unlock a higher limit.")
            ], -1))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(DocumentTextIcon), {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (isTemplatesModalOpen.value = true)),
            class: "w-6 h-6 cursor-pointer text-gray-400 hover:text-gray-600"
          }),
          (_ctx.isWorkflow)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 0,
                classes: ['rounded-md'],
                disabled: isCreatingMessage.value,
                onClick: createMessage
              }, {
                default: _withCtx(() => _cache[10] || (_cache[10] = [
                  _createTextVNode("Save")
                ])),
                _: 1,
                __: [10]
              }, 8, ["disabled"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(GgmsEmojiPicker, { onSendEmoji: setEmojiInTextarea }),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(GgmsButton, {
                    classes: ['rounded-md rounded-r-none'],
                    disabled: isCreatingMessage.value || costExceeded.value || isButtonDisabled.value,
                    onClick: createMessage
                  }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode("Send Text Message")
                    ])),
                    _: 1,
                    __: [11]
                  }, 8, ["disabled"]),
                  _createVNode(GgmsButton, {
                    classes: ['px-2 rounded-md rounded-l-none'],
                    disabled: isButtonDisabled.value,
                    onClick: openScheduleMessageModal
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(CalendarIcon), { class: "w-6 h-6 text-white" })
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ])
              ]))
        ])
      ]),
      (costExceeded.value)
        ? (_openBlock(), _createBlock(GgmsWarningBanner, {
            key: 1,
            "class-name": "w-full"
          }, {
            default: _withCtx(() => [
              _cache[12] || (_cache[12] = _createTextVNode(" You don't have enough points to send this message. Please top up first from the ")),
              _createElementVNode("span", {
                class: "font-medium text-primary-color-500 cursor-pointer",
                onClick: _cache[4] || (_cache[4] = () => _unref(router).push(`/billing`))
              }, " Plans & Billing "),
              _cache[13] || (_cache[13] = _createTextVNode(" section "))
            ]),
            _: 1,
            __: [12,13]
          }))
        : _createCommentVNode("", true),
      _createVNode(MessageTemplatesModal, {
        open: isTemplatesModalOpen.value,
        "template-title": "Text Message",
        onCloseModal: _cache[5] || (_cache[5] = ($event: any) => (isTemplatesModalOpen.value = false)),
        "onUpdate:modelValue": updateTemplate,
        size: "3xl"
      }, null, 8, ["open"])
    ], 2),
    _createVNode(GgmsModal, {
      open: isOpenScheduleMessageModal.value,
      onCloseModal: closeScheduleMessageModal
    }, _createSlots({
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_unref(CalendarIcon), { class: "h-6 w-6 text-primary-color-600" })
          ]),
          _cache[14] || (_cache[14] = _createElementVNode("h1", { class: "text-lg leading-6 font-medium text-gray-900" }, "Schedule Text Message", -1))
        ])
      ]),
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scheduleOptions.value, (schedule) => {
          return (_openBlock(), _createElementBlock("div", {
            key: schedule.text
          }, [
            _createElementVNode("div", {
              class: "flex justify-between items-center cursor-pointer",
              onClick: ($event: any) => (saveScheduleMessage(schedule.date))
            }, [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(ClockIcon), { class: "h-5 w-5 text-primary-color-600 mr-3" }),
                _createElementVNode("p", _hoisted_11, _toDisplayString(schedule.text), 1)
              ]),
              _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(formatters).formatHumanReadableDate(schedule.date, true)), 1)
            ], 8, _hoisted_9),
            _createVNode(GgmsSeparator, { class: "my-4" })
          ]))
        }), 128)),
        _createElementVNode("div", null, [
          _createElementVNode("button", {
            class: "flex items-center w-fit border-none bg-transparent",
            onClick: toggleCustomSchedule
          }, [
            _createVNode(_unref(CalendarIcon), { class: "h-5 w-5 text-primary-color-600 mr-3" }),
            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "text-base font-medium text-primary-color-600" }, "Pick Date & Time", -1))
          ]),
          (isOpenCustomSchedule.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createVNode(GgmsCalendar, {
                  modelValue: customScheduleDate.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((customScheduleDate).value = $event)),
                  position: "below",
                  showTime: false,
                  showDate: true,
                  minDate: "today",
                  iconStart: _unref(CalendarIcon),
                  class: "w-full"
                }, null, 8, ["modelValue", "iconStart"]),
                _createVNode(GgmsCalendar, {
                  modelValue: customScheduleTime.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((customScheduleTime).value = $event)),
                  position: "below",
                  minDate: minHour.value,
                  showTime: true,
                  showDate: false,
                  iconStart: _unref(ClockIcon),
                  class: "w-full"
                }, null, 8, ["modelValue", "minDate", "iconStart"])
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 2
    }, [
      (isOpenCustomSchedule.value)
        ? {
            name: "under",
            fn: _withCtx(() => [
              _createElementVNode("div", _hoisted_14, [
                _createVNode(GgmsButton, {
                  styleType: "secondary",
                  class: "mr-3",
                  onClick: closeScheduleMessageModal
                }, {
                  default: _withCtx(() => _cache[16] || (_cache[16] = [
                    _createTextVNode("Cancel")
                  ])),
                  _: 1,
                  __: [16]
                }),
                _createVNode(GgmsButton, {
                  disabled: isScheduleButtonDisabled.value,
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (saveScheduleMessage()))
                }, {
                  default: _withCtx(() => _cache[17] || (_cache[17] = [
                    _createTextVNode("Done")
                  ])),
                  _: 1,
                  __: [17]
                }, 8, ["disabled"])
              ])
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["open"]),
    _createVNode(GgmsModal, {
      open: isMessageLimitModalOpen.value,
      size: "sm",
      onCloseModal: closeMessageLimitModal
    }, {
      header: _withCtx(() => _cache[18] || (_cache[18] = [
        _createElementVNode("div", null, null, -1)
      ])),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_unref(ExclamationCircleIcon), { class: "h-6 w-6 text-yellow-600" })
          ]),
          _cache[20] || (_cache[20] = _createElementVNode("h1", { class: "text-lg leading-6 font-medium text-gray-900 mb-2" }, "Failed To Send Text Message", -1)),
          _cache[21] || (_cache[21] = _createElementVNode("p", { class: "text-center text-sm font-normal text-gray-500 mb-6" }, " You have exceeded your Agency daily message sending limit of 100. Set up a Business Profile to unlock a higher limit ", -1)),
          _createVNode(GgmsButton, {
            styleType: "primary",
            onClick: closeMessageLimitModal,
            class: "w-full"
          }, {
            default: _withCtx(() => _cache[19] || (_cache[19] = [
              _createTextVNode("Set Up Business Profile")
            ])),
            _: 1,
            __: [19]
          })
        ])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})